/**
 * Featured Collection Tabs
 * Web Component: <featured-collection-tabs>
 *
 * Handles:
 * - Tab switching between collection panels
 * - Swiper carousel navigation with arrow buttons
 * - Lazy Swiper init (only when panel becomes visible)
 */
if (!customElements.get('featured-collection-tabs')) {
  class FeaturedCollectionTabs extends HTMLElement {
    constructor() {
      super();
      this.currentTab = 0;
      this.swipers = {};
    }

    connectedCallback() {
      this.tabs = this.querySelectorAll('[data-tab]');
      this.panels = this.querySelectorAll('[data-tab-panel]');
      this.prevButtons = this.querySelectorAll('[data-prev]');
      this.nextButtons = this.querySelectorAll('[data-next]');

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.switchTab(index));
      });

      this.prevButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.slidePrev());
      });

      this.nextButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.slideNext());
      });

      if (!this.dataset.deferInit) {
        this.initActiveSwiper();
      }
    }

    disconnectedCallback() {
      Object.values(this.swipers).forEach((swiper) => {
        if (swiper) swiper.destroy(true, true);
      });
      this.swipers = {};
    }

    waitForSwiper(callback) {
      if (typeof Swiper !== 'undefined') return callback();
      if (window.loadSwiper) {
        window.loadSwiper().then(callback);
        return;
      }
      var check = setInterval(function () {
        if (typeof Swiper !== 'undefined') {
          clearInterval(check);
          callback();
        }
      }, 100);
    }

    initSwiperForPanel(index) {
      if (this.swipers[index]) return;

      var panel = this.panels[index];
      if (!panel) return;

      var swiperEl = panel.querySelector('.featured-collection__slider');
      if (!swiperEl) return;

      var self = this;
      var mobileCols = parseInt(this.dataset.mobileColumns, 10) || 2;
      var desktopCols = parseInt(this.dataset.columns, 10) || 4;
      var mobileGap = parseInt(this.dataset.mobileGap, 10) || 16;
      var desktopGap = parseInt(this.dataset.gap, 10) || 32;

      this.swipers[index] = new Swiper(swiperEl, {
        slidesPerView: mobileCols,
        spaceBetween: mobileGap,
        speed: 500,
        breakpoints: {
          992: {
            slidesPerView: desktopCols,
            spaceBetween: desktopGap
          }
        },
        on: {
          slideChange: function () {
            self.updateArrowStates();
          }
        }
      });
    }

    initActiveSwiper() {
      var self = this;
      this.waitForSwiper(function () {
        self.initSwiperForPanel(self.currentTab);
        self.updateArrowStates();
      });
    }

    getActiveSwiper() {
      return this.swipers[this.currentTab] || null;
    }

    switchTab(index) {
      if (index === this.currentTab) return;

      this.tabs.forEach((tab, i) => {
        tab.classList.toggle('is-active', i === index);
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      this.panels.forEach((panel, i) => {
        panel.classList.toggle('is-active', i === index);
      });

      this.currentTab = index;

      var self = this;
      this.waitForSwiper(function () {
        if (!self.swipers[index]) {
          self.initSwiperForPanel(index);
        } else {
          self.swipers[index].update();
        }
        self.updateArrowStates();
      });
    }

    slidePrev() {
      var swiper = this.getActiveSwiper();
      if (swiper) swiper.slidePrev();
    }

    slideNext() {
      var swiper = this.getActiveSwiper();
      if (swiper) swiper.slideNext();
    }

    updateArrowStates() {
      var swiper = this.getActiveSwiper();
      var atStart = !swiper || swiper.isBeginning;
      var atEnd = !swiper || swiper.isEnd;

      this.prevButtons.forEach(function (btn) {
        btn.disabled = atStart;
      });

      this.nextButtons.forEach(function (btn) {
        btn.disabled = atEnd;
      });
    }
  }

  customElements.define('featured-collection-tabs', FeaturedCollectionTabs);
}
